// src/components/resources/glossary/GlossaryFaqs.tsx

'use client'

import { useState, useId } from 'react'
import RiArrowDownSLine from '~/icons/remix/generated/RiArrowDownSLine'
import { slugify } from '~/lib/utils/formatters'

/**
 * Glossary-specific FAQ component.
 *
 * Replaces the heavier `FaqsClient` (Radix Accordion with card-per-item +
 * shadows + rotating "+/×" icon) on glossary term pages, which collectively
 * read as a stack of boxed cards and use a dated visual language.
 *
 * Design notes:
 *   - Flat divider-separated list. No per-item card backgrounds/shadows.
 *     This integrates with the surrounding article layout instead of
 *     interrupting it with a stack of inset panels.
 *   - Single chevron icon (RiArrowDownSLine) that rotates 180° on open.
 *     One consistent affordance, not two visual states (+/×).
 *   - Independent open state per item via `useState<Set<string>>`. Multiple
 *     items can be open simultaneously, matching the prior FaqsClient
 *     behavior so SEO scrapers + users keyboard-navigating with arrows
 *     see no behavior change.
 *   - Stable per-question DOM ids derived from `slugify(question)`, with
 *     a fallback bucket for any question that slugifies to empty (the
 *     build validator rejects empty-slug questions, so this fallback is
 *     defense-in-depth only).
 *   - aria-controls + aria-expanded on the button, region + aria-labelledby
 *     on the answer panel. Same a11y posture as Radix Accordion.
 *
 * This is a client component because of the open-state interactivity.
 * For SSR / no-JS readers, the server-native analog is `FaqsSection` in
 * `GlossaryTermServerContent.tsx` — same markup structure but using native
 * `<details>`/`<summary>` so the content is crawlable + togglable without
 * React. (Blog posts use the same `<details>` pattern via `BlogPostFaqs`.)
 */

export interface GlossaryFaqItem {
  question: string
  answer: string
}

interface GlossaryFaqsProps {
  items: readonly GlossaryFaqItem[]
  /** Required scope so anchor ids don't collide if multiple instances live on one page. Typically the term slug. */
  idPrefix: string
  className?: string
}

export function GlossaryFaqs({
  items,
  idPrefix,
  className = ''
}: GlossaryFaqsProps): JSX.Element | null {
  // Initialize open-state. Default: all collapsed. The prior FaqsClient
  // also defaulted to collapsed; preserving that means landing on a
  // metric page with 5 FAQs doesn't push the related-terms sidebar way
  // below the fold on first paint.
  const [openSet, setOpenSet] = useState<Set<string>>(() => new Set())
  const reactId = useId()

  if (!items || items.length === 0) return null

  const toggle = (key: string): void => {
    setOpenSet((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <div className={`divide-y divide-gray-200 dark:divide-gray-800 ${className}`.trim()}>
      {items.map((item, index) => {
        const slugged = slugify(item.question, '-') || `faq-${index}`
        const key = `${idPrefix}-${slugged}`
        // Fallback to React's useId-derived id if idPrefix isn't supplied;
        // the question slug remains stable across reorders (build validator
        // enforces per-term question uniqueness in slugified space).
        const questionId = `${key}-q`
        const panelId = `${key}-a`
        const isOpen = openSet.has(key)

        return (
          <div key={`${reactId}-${slugged}-${index}`} className="group">
            <h3 className="m-0">
              <button
                type="button"
                id={questionId}
                aria-controls={panelId}
                aria-expanded={isOpen}
                onClick={() => toggle(key)}
                className="flex w-full items-start justify-between gap-4 py-5 text-left text-base font-medium text-gray-900 transition-colors hover:text-indigo-700 dark:text-gray-100 dark:hover:text-indigo-300 sm:text-lg"
              >
                <span className="leading-snug">{item.question}</span>
                <RiArrowDownSLine
                  aria-hidden="true"
                  className={`mt-0.5 size-5 shrink-0 text-gray-400 transition-transform duration-200 dark:text-gray-500 ${
                    isOpen ? 'rotate-180 text-indigo-600 dark:text-indigo-400' : ''
                  }`}
                />
              </button>
            </h3>
            <div
              id={panelId}
              role="region"
              aria-labelledby={questionId}
              hidden={!isOpen}
              className="pb-6 pr-9 text-base leading-7 text-gray-600 dark:text-gray-400"
            >
              {item.answer}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default GlossaryFaqs
